import React, { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Warehouse, ArrowDownToLine, ArrowUpFromLine, AlertTriangle, RotateCcw } from "lucide-react";

const TYPE_META = {
  in: { color: "#10B981", icon: ArrowDownToLine, label: "In" },
  out: { color: "#F59E0B", icon: ArrowUpFromLine, label: "Out" },
  transfer: { color: "#3B82F6", icon: RotateCcw, label: "Transfer" },
  damage: { color: "#EF4444", icon: AlertTriangle, label: "Damage" },
  waste: { color: "#EF4444", icon: AlertTriangle, label: "Waste" },
  return: { color: "#A78BFA", icon: RotateCcw, label: "Return" },
};

export default function WarehousePage() {
  const [equipment, setEquipment] = useState([]);
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    api.get("/warehouse/equipment").then((r) => setEquipment(r.data));
    api.get("/warehouse/movements").then((r) => setMovements(r.data));
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="warehouse-page">
      <div className="mb-8">
        <div className="label-eyebrow">Central Warehouse</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Equipment & Movements</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-3">
            <Warehouse className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Equipment Inventory · {equipment.length}</div>
          </div>
          <div className="divide-y divide-[#1a1a1a] max-h-[420px] overflow-y-auto">
            {equipment.map((e) => (
              <div key={e.id} className="py-3 flex items-center justify-between" data-testid={`eq-${e.id}`}>
                <div>
                  <div className="text-sm">{e.name}</div>
                  <div className="text-xs text-zinc-500 capitalize mt-0.5">{e.category}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-lg" style={{ color: e.qty <= e.reorder_level ? "#F59E0B" : "#FAFAFA" }}>{e.qty} {e.unit}</div>
                  <div className="text-[10px] text-zinc-500">@reorder {e.reorder_level}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-lux p-6">
          <div className="flex items-center gap-2 mb-3">
            <RotateCcw className="w-4 h-4 text-[#D4AF37]" />
            <div className="label-eyebrow">Recent Movements · {movements.length}</div>
          </div>
          <div className="divide-y divide-[#1a1a1a] max-h-[420px] overflow-y-auto">
            {movements.length === 0 && <div className="py-8 text-center text-zinc-600 text-sm">No movements yet</div>}
            {movements.map((m) => {
              const meta = TYPE_META[m.type] || TYPE_META.in;
              const Icon = meta.icon;
              return (
                <div key={m.id} className="py-3 flex items-center gap-3" data-testid={`mov-${m.id}`}>
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: `${meta.color}15`, color: meta.color }}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm">{m.item_name || m.item_id}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{meta.label} · {fmtDate(m.at)} {m.note ? `· ${m.note}` : ""}</div>
                  </div>
                  <div className="font-display text-lg" style={{ color: meta.color }}>{m.qty}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
